<template>
  <div
    ref="quickAddMobile"
    class="quick-add-mobile"
    js-overlay="scrollElement"
  >
    <button
      :aria-label="$string('accessibility.close')"
      class="quick-add-mobile__close icon-button"
      @click.prevent="closeOverlay(namespace)"
    >
      <span v-html="$icon('enclosed.close')" />
    </button>

    <div class="quick-add-mobile__details-container container container--padding-top">
      <div class="quick-add-mobile__product-image">
        <responsive-image
          :force-update="true"
          :image="variantImage"
          :ratio="'76:96'"
          block-class="quick-add-mobile"
          min-max="100-200"
        />
      </div>

      <div class="quick-add-mobile__details">
        <div class="quick-add-mobile__title body-2">
          {{ product.title }}
        </div>

        <div class="quick-add-mobile__title body-2">
          <product-prices
            :available="product?.available"
            :compare-at-price="compareAtPrice"
            :price="price"
            :reverse="true"
            :style="{ animationDelay: animationDelay }"
            block-class="quick-add-mobile"
          />
        </div>

        <div
          v-if="swatchLabel"
          class="quick-add-mobile__details--bottom caption"
        >
          {{ swatchLabel }}
        </div>

        <div
          v-if="swatchColors"
          class="quick-add-mobile__swatches"
          :class="{
            'quick-add-mobile__details--bottom': !swatchLabel
          }"
        >
          <swiper
            :instance-name="`quick-add-mobile-colors-${product.id}`"
            :modules="modules"
            :slides-per-view="'auto'"
            :space-between="12"
            class="quick-add-mobile__swatches-carousel"
          >
            <swiper-slide
              v-for="(color, swatchIndex) in swatchColors.values"
              :id="`quick-add-mobile-color-${swatchIndex}-${product.id}`"
              :key="`quick-add-mobile-color-${swatchIndex}-${product.id}`"
              class="quick-add-mobile__swatch-slide"
            >
              <div class="quick-add-mobile__swatch">
                <color-swatch
                  :product="product"
                  :color="color"
                  :tooltip="false"
                  :is-active="isSelectedOption(swatchColors.index, color)"
                  :swatch-image="swatchImages[color]?.src"
                  @click.prevent="updateSelectedOptions(swatchColors.index, color)"
                />
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>

    <div
      v-if="nonColors"
      class="quick-add-mobile__sizes body-2"
    >
      <swiper
        :instance-name="`quick-add-mobile-sizes-${product.id}`"
        :modules="modules"
        :slides-per-view="'auto'"
        :space-between="8"
      >
        <swiper-slide
          v-for="(option, optionIndex) in nonColors.values"
          :id="`quick-add-mobile-size-${optionIndex}-${product.id}`"
          :key="`quick-add-mobile-size-${optionIndex}-${product.id}`"
          class="quick-add-mobile__size-slide"
        >
          <button
            :class="{
              'quick-add-mobile__size--active': isSelectedOption(nonColors.index, option),
              'quick-add-mobile__size--disabled': !checkAvailability(nonColors.index, option)
            }"
            class="quick-add-mobile__size body-2"
            @click.prevent="updateSelectedOptions(nonColors.index, option)"
          >
            {{ option }}
          </button>
        </swiper-slide>
      </swiper>
    </div>

    <div class="quick-add-mobile__actions container">
      <btn
        :disabled="!selectedVariant?.available"
        :icon="false"
        :label="ctaLabel"
        :state="isAddingToCart"
        class="quick-add-mobile__add-cart"
        :class="{
          'quick-add-mobile__add-cart--shake': wishlistClicked
        }"
        modifiers="secondary center block"
        state-icon="misc.loading"
        @click.prevent="handleAddToCart"
      />
      <wishlist-button
        v-if="wishlist"
        :product-id="product?.id"
        :variant-id="selectedVariant?.id"
        block-class="quick-add-mobile"
        icon="enclosed.heartLarge"
        @click="handleDisabledWishlist"
      />
    </div>

    <div
      class="quick-add-mobile__footer text-center container container--padding-bottom"
    >
      <a
        :href="product.url"
        class="quick-add-mobile__view text-link--bold"
      >
        {{ $string('product.quick_add.view_details') }}
      </a>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Quick add overlay (quick-add-mobile-overlay)
 * -----------------------------------------------------------------------------
 * Quick add overlay modal for mobile devices.
 *
 * @param {Boolean} isActive - Is overlay active?
 * @param {Object} preselectedOptions - Open the overlay with the selected
 * options.
 * @param {Object} product - The product that opened the quick add overlay.
 * @param {String} productCardColor - The selected color on the product card.
 * @param {String} namespace - A unique namespace for this quick add overlay.
 * @param {Boolean} wishlist - Enable wishlist button.
 */
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y } from 'swiper'

import { capitalize } from '~/helpers/general'
import { cloneObjectArray } from '~/helpers/manipulate'
import * as productHelpers from '~/helpers/product'
import timings from '~/config/timings'

export default {
  name: 'QuickAddMobile',

  components: {
    Swiper,
    SwiperSlide,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    preselectedOptions: {
      type: Array,
      default: () => ([]),
    },
    product: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    productCardColor: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
    },
    wishlist: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update-selected-variant'],

  setup() {
    return {
      modules: [A11y],
    }
  },

  data() {
    return {
      type: 'filter',
      colorIndex: -1,
      isAddingToCart: false,
      selectedOptions: [],
      nonColors: productHelpers.getNonColors(this.product),
      swatchColors: productHelpers.getColors(this.product),
      wishlistClicked: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    swatchColors(){
      return productHelpers.getColors(this.product);
    },

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return `${timings.xs}s`
    },

    /**
     * Compute the variant compare at price. Defaults to product compare at
     * price.
     * @returns {Number} - The price in cents.
     */
    compareAtPrice() {
      return this.selectedVariant?.compareAtPrice.amount ||
        this.product.compareAtPrice.amount
    },

    /**
     * Compute the add to cart button label.
     * @returns {String}
     */
    ctaLabel() {
      if (this.selectedVariant?.available) {
        return this.$string('product.cta.add_to_cart')
      }

      const replace = this.selectedOptions.find(option => !option.value)?.name

      if (replace) {
        return this.$string('product.swatch.select_option', {
          replace: {
            option: capitalize(replace),
          },
        })
      }

      if (!this.selectedVariant) {
        return this.$string('product.cta.unavailable')
      }

      return this.$string('product.cta.sold_out')
    },

    /**
     * Compute the variant price. Defaults to product price.
     * @returns {Number} - The price in cents.
     */
    price() {
      return this.selectedVariant?.price.amount ||
        this.product.price.amount
    },

    /**
     * Compute the selected variant based on the selected options.
     * @returns {Object}
     */
    selectedVariant() {
      return productHelpers.getVariantByTitle(
        this.product, this.selectedOptions,
      )
    },

    /**
     * Compute a formatted color swatch label.
     * @returns {String}
     */
    swatchLabel() {
      const value = this.selectedOptions[this.colorIndex]?.value
      return value ?
        `${this.$string('product.swatch.color_option')}: ${value}` :
        ''
    },

    /**
     * Compute an object of images associated with swatches. The colors are
     * the object keys.
     * @returns {Object}
     */
    swatchImages() {
      return productHelpers.getSwatchImages(
        this.product, this.swatchColors.values,
      )
    },

    /**
     * Compute the selected variant image. Defaults to featured image.
     * @returns {Object}
     */
    variantImage() {
      const color = this.selectedOptions[this.colorIndex]?.value
      if (color && (!this.selectedVariant || !this.selectedVariant.image)) {
        const colorImages = productHelpers.getVariantImagesByColor(this.product, color)

        if (colorImages.length) {
          return colorImages[0]
        }
      }
      return productHelpers.getVariantImage(this.product, this.selectedVariant)
    },
  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay(this.namespace)
    },

    productCardColor() {
      this.updateColor(this.productCardColor)
    },

    selectedVariant() {
      if (this.selectedVariant) {
        this.$emit('update-selected-variant', this.selectedVariant)
      }
    },
  },

  mounted() {
    this.updateFocus()

    this.selectedOptions = this.preselectedOptions.length > 0 ?
      cloneObjectArray(this.preselectedOptions) :
      this.product.options.map(
        option => {
          if (option.name.match(/color/gi)) {
            this.colorIndex = option.index
          }
          return { name: option.name, value: '' }
        },
      )

    if (this.productCardColor) {
      this.updateColor(this.productCardColor)
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    checkAvailability(index, optionValue) {
      const options = []
      this.selectedOptions.forEach((option, _index) => {
        options[_index] = option.value
      })
      options[index] = optionValue
      return productHelpers.isVariantOptionAvailable(this.product, options)
    },

    /**
     * Add to cart and open the cart drawer.
     */
    async handleAddToCart() {
      if (this.isAddingToCart) {
        return
      }
      try {
        const properties = productHelpers.getLineItemProperties(this.product, this.selectedVariant)
        this.isAddingToCart = true
        await this.addItem([
          {
            id: this.selectedVariant?.id,
            quantity: 1,
            properties,
            variant: this.selectedVariant,
            product: this.product,
          },
        ])

        let type = 'filter'

        if (window.cnvs.page.type === 'search') {
          type = 'search'
        }

        this.type = type

        this.isAddingToCart = false
        this.openOverlay({
          namespace: 'cartDrawer',
          ignoreDismissed: true,
          component: 'cart-drawer',
        })
        this.$gtmTrack('AddtoCart', this.$variable('analytics.quickAdd'))
      }
      catch (error) {
        this.isAddingToCart = false
        console.error('[QuickAddMobile]', error)
      }
    },

    /**
     * Check if the given option is selected.
     * @param {Number} index - The index of the option to check.
     * @param {String} value - The value to check.
     * @returns {boolean}
     */
    isSelectedOption(index, value) {
      return this.selectedOptions[index]?.value === value
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', this.namespace)
      })
    },

    /**
     * Update the color in selected options.
     * @param {String} value - Color value.
     */
    updateColor(value) {
      if (this.colorIndex < 0) {
        return
      }
      this.selectedOptions[this.colorIndex].value = value || ''
    },

    /**
     * Update specific options with a new value.
     * @param {Number} index - The index of the option to update.
     * @param {String} value - The new option value.
     */
    updateSelectedOptions(index, value) {
      this.selectedOptions[index].value = value
    },

    /**
     * Trigger the shake animation if selectedVariant doesn't exist.
     */
    handleDisabledWishlist() {
      if (this.selectedVariant) {
        this.wishlistClicked = false
        return
      }

      this.wishlistClicked = true

      setTimeout(() => this.wishlistClicked = false, timings.l)
    },
  },
}
</script>

<style lang="scss">
@import './quick-add-mobile';
</style>
